import React, { useState } from 'react';
import { SubscriptionButton } from '../components/SubscriptionButton';
import { useSubscriptionPermissions } from '../hooks/useSubscriptionPermissions';
import { useTrialEligibility } from '../hooks/useTrialEligibility';
import { Check, Star } from 'lucide-react';
import { getPlanVariant } from '../stripe-config';
import type { PlanId } from '../stripe-config';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface PlanVariantDetails {
  productId?: string;
  priceId?: string;
  price: number;
  currency: string;
}

interface Plan {
  id: string;
  name: string;
  subtitle: string;
  monthly: PlanVariantDetails;
  yearly: PlanVariantDetails;
  description: string;
  features: PlanFeature[];
  popular?: boolean;
}

const PagePricing: React.FC = () => {
  const [isYearly, setIsYearly] = useState(false);
  const { subscription, loading: subscriptionLoading } = useSubscriptionPermissions();
  const { eligible: isTrialEligible, loading: trialEligibilityLoading } = useTrialEligibility();
  // Normalize current plan to lowercase for comparison (database stores 'Boost', 'Scale', 'Lunch' with capitals)
  const currentPlan = subscription?.plan_name?.toLowerCase() || 'lunch';

  const buildPlanVariant = (planId: PlanId, interval: 'month' | 'year'): PlanVariantDetails => {
    const variant = getPlanVariant(planId, interval);
    const priceValue = variant ? Number(variant.price.replace(/[^0-9.]/g, '')) : 0;

    return {
      productId: variant?.productId,
      priceId: variant?.priceId,
      price: Number.isFinite(priceValue) ? priceValue : 0,
      currency: variant?.currency ?? 'USD'
    };
  };

  const freeVariant: PlanVariantDetails = {
    price: 0,
    currency: 'USD'
  };

  const boostMonthly = buildPlanVariant('boost', 'month');
  const boostYearly = buildPlanVariant('boost', 'year');
  const scaleMonthly = buildPlanVariant('scale', 'month');
  const scaleYearly = buildPlanVariant('scale', 'year');

  const plans: Plan[] = [
    {
      id: 'lunch',
      name: 'Lunch',
      subtitle: 'Perfect to get started',
      monthly: freeVariant,
      yearly: freeVariant,
      description: 'The essential tools to begin as a freelancer',
      features: [
        { text: 'Up to 5 clients', included: true },
        { text: '10 orders per month', included: true },
        { text: 'Dashboard, Clients, Orders', included: true },
        { text: 'Email support', included: true },
        { text: 'Calendar access', included: false },
        { text: 'Workboard access', included: false },
        { text: 'Statistics', included: false },
        { text: 'Invoices', included: false },
        { text: 'AI Assistant', included: false }
      ]
    },
    {
      id: 'boost',
      name: 'Boost',
      subtitle: 'For active freelancers',
      monthly: boostMonthly,
      yearly: boostYearly,
      description: 'More pages, more power, more growth',
      features: [
        { text: 'Unlimited clients & orders', included: true },
        { text: 'Dashboard, Clients, Orders', included: true },
        { text: 'Calendar access', included: true },
        { text: 'Workboard access', included: true },
        { text: 'Priority support', included: true },
        { text: 'Statistics', included: false },
        { text: 'Invoices', included: false },
        { text: 'AI Assistant', included: false }
      ],
      popular: true
    },
    {
      id: 'scale',
      name: 'Scale',
      subtitle: 'For advanced freelancers',
      monthly: scaleMonthly,
      yearly: scaleYearly,
      description: 'The complete package to reach the next level',
      features: [
        { text: 'Everything in Boost', included: true },
        { text: 'Advanced statistics', included: true },
        { text: 'Advanced invoices', included: true },
        { text: 'AI Assistant', included: true },
        { text: 'Priority support', included: true }
      ]
    }
  ];

  const formatPrice = (price: number, currency: string = 'USD') => {
    if (price === 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold theme-text-primary mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg theme-text-secondary max-w-2xl mx-auto">
            Unlock the full potential of your freelance business with our plans optimized for every stage of your growth.
          </p>
        </div>

        {/* Toggle Billing */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span className={`text-base font-medium ${!isYearly ? 'theme-text-primary' : 'theme-text-tertiary'}`}>
            Monthly
          </span>
          <button
            onClick={() => setIsYearly(!isYearly)}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              isYearly ? 'bg-[#9c68f2]' : 'bg-slate-300 dark:bg-slate-700'
            }`}
          >
            <div
              className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-transform shadow-md ${
                isYearly ? 'translate-x-7' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span className={`text-base font-medium ${isYearly ? 'theme-text-primary' : 'theme-text-tertiary'}`}>
            Yearly
          </span>
          <span className={`text-sm transition-opacity duration-200 ${isYearly ? 'theme-text-secondary opacity-100' : 'theme-text-tertiary opacity-30'}`}>
            Save <span className={`font-semibold ${isYearly ? 'text-[#9c68f2]' : 'theme-text-tertiary'}`}>20%</span>
          </span>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => {
            const priceVariant = isYearly ? plan.yearly : plan.monthly;
            const price = priceVariant.price;
            const currency = priceVariant.currency;
            const period = isYearly ? 'year' : 'month';
            // Compare plans case-insensitively (database: 'Boost'/'Scale'/'Lunch', frontend: 'boost'/'scale'/'lunch')
            const isCurrentPlan = !subscriptionLoading && currentPlan === plan.id.toLowerCase();
            
            return (
              <div
                key={plan.id}
                className={`relative theme-card-bg rounded-2xl border-2 transition-all ${
                  plan.popular
                    ? 'border-[#9c68f2] shadow-2xl shadow-[#9c68f2]/30'
                    : 'border-[rgb(var(--border))] shadow-xl shadow-slate-900/10 dark:shadow-slate-900/30 hover:border-[rgb(var(--border-strong))] hover:shadow-2xl'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-[#9c68f2] to-[#422ca5] text-[rgb(var(--fg))] px-4 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      Popular
                    </div>
                  </div>
                )}

                <div className="p-8">
                  {/* Plan Header */}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold theme-text-primary mb-1">{plan.name}</h3>
                    <p className="text-sm theme-text-secondary mb-4">{plan.subtitle}</p>
                    
                    {/* Price */}
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-slate-900 dark:text-[rgb(var(--fg))]">
                        {formatPrice(price)}
                      </span>
                      {price > 0 && (
                        <span className="theme-text-tertiary text-lg">/{period}</span>
                      )}
                    </div>
                    
                    {/* Description */}
                    <p className="text-sm theme-text-secondary mb-6">{plan.description}</p>
                    
                    {/* Button - Just below price, above features */}
                    {isCurrentPlan ? (
                      <button
                        disabled
                        className="w-full py-3 px-4 rounded-lg font-medium cursor-not-allowed bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700 opacity-90"
                      >
                        ✓ Current Plan
                      </button>
                    ) : plan.id === 'lunch' ? (
                      <button
                        disabled
                        className="w-full py-3 px-4 rounded-lg font-medium cursor-not-allowed theme-btn-secondary opacity-70"
                      >
                        Free Plan
                      </button>
                    ) : (
                      <div className="w-full">
                        <SubscriptionButton
                          priceId={priceVariant.priceId}
                          productId={priceVariant.productId}
                          planName={plan.name}
                          amount={`${formatPrice(price, currency)}/${period}`}
                          billingInterval={period as 'month' | 'year'}
                          className="w-full py-3 px-4 bg-gradient-to-r from-[#9c68f2] to-[#422ca5] hover:from-[#8a5cf0] hover:to-[#3a2590] text-[rgb(var(--fg))] rounded-lg font-medium transition-all"
                          trialDays={
                            plan.id === 'boost' && isTrialEligible && !trialEligibilityLoading
                              ? 7
                              : undefined
                          }
                          label={
                            plan.id === 'boost' && isTrialEligible && !trialEligibilityLoading
                              ? 'Start 7-day free trial'
                              : `Subscribe to ${plan.name}`
                          }
                          onSuccess={() => {
                            console.log(`Successfully subscribed to ${plan.name}`);
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Features List */}
                  <div className="space-y-3 pt-6 border-t border-[rgb(var(--border))]">
                    {plan.features
                      .filter(feature => feature.included)
                      .map((feature, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm theme-text-secondary">
                            {feature.text}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PagePricing;
